import { useEffect, useState } from "react";
import api, { currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KommaLogo } from "@/components/komma/KommaLogo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    processing: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    disbursed: "bg-komma-gold/15 text-komma-navy border-komma-gold/40",
    rejected: "bg-red-50 text-red-700 border-red-200",
    closed: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function CustomerPortal() {
    const { user } = useAuth();
    const [loans, setLoans] = useState([]);
    const [payments, setPayments] = useState([]);
    const [selectedLoan, setSelectedLoan] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [l, p] = await Promise.all([api.get("/loans"), api.get("/payments")]);
                setLoans(l.data);
                setPayments(p.data);
                if (l.data[0]) setSelectedLoan(l.data[0].id);
            } catch {}
        })();
    }, []);

    const download = async (loanId, number) => {
        try {
            const res = await api.get(`/loans/${loanId}/sanction-letter`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = `Sanction-${number}.pdf`; a.click();
            URL.revokeObjectURL(url);
            toast.success("Sanction letter downloaded");
        } catch { toast.error("Could not download"); }
    };

    const loanPayments = payments.filter((p) => p.loan_id === selectedLoan);
    const paidCount = loanPayments.filter((p) => p.paid).length;
    const upcoming = loanPayments.find((p) => !p.paid);
    const totalDue = loanPayments.filter((p) => !p.paid).reduce((s, p) => s + p.amount, 0);
    const activeLoan = loans.find((l) => l.id === selectedLoan);

    return (
        <div className="space-y-6" data-testid="customer-portal">
            <div className="komma-card p-8 relative overflow-hidden">
                <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-komma-gold/10" />
                <div className="flex items-center gap-4 relative">
                    <div className="bg-komma-cream border border-komma-gold/40 rounded-xl p-3">
                        <KommaLogo size={56} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-komma-gold">Customer Portal</p>
                        <h1 className="font-serif text-3xl text-komma-navy mt-1">Welcome, {user.name?.split(" ")[0]}</h1>
                        <p className="text-sm text-gray-600 mt-1">Track your loans, EMI schedule and download your sanction letter — anytime.</p>
                    </div>
                </div>
            </div>

            {loans.length === 0 ? (
                <div className="komma-card p-12 text-center">
                    <ShieldCheck className="mx-auto text-komma-gold" size={40} />
                    <p className="font-serif text-xl text-komma-navy mt-3">No loans linked yet</p>
                    <p className="text-sm text-gray-500 mt-1">Once your loan file is created, it will appear here automatically.</p>
                </div>
            ) : (
                <>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">My Loans</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {loans.map((l) => (
                                <button key={l.id} onClick={() => setSelectedLoan(l.id)}
                                    data-testid={`customer-loan-${l.id}`}
                                    className={`komma-card p-5 text-left transition-all ${selectedLoan === l.id ? "border-komma-gold ring-2 ring-komma-gold/30" : ""}`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest text-komma-gold">{l.loan_number}</p>
                                            <p className="font-serif text-lg text-komma-navy mt-1 capitalize">{l.loan_type} Loan</p>
                                        </div>
                                        <Badge variant="outline" className={`${STATUS_STYLES[l.status]} capitalize`}>{l.status}</Badge>
                                    </div>
                                    <div className="komma-gold-hairline my-3" />
                                    <div className="grid grid-cols-2 text-sm">
                                        <div>
                                            <p className="text-[10px] uppercase text-gray-500 tracking-widest">Amount</p>
                                            <p className="font-serif text-lg text-komma-navy">{currency(l.amount)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase text-gray-500 tracking-widest">EMI</p>
                                            <p className="font-serif text-lg text-komma-navy">{currency(l.emi)}</p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {activeLoan && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="komma-card p-6 lg:col-span-1">
                                <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">Loan Summary</p>
                                <div className="komma-gold-hairline my-4" />
                                <Row label="Loan #" value={activeLoan.loan_number} />
                                <Row label="Type" value={<span className="capitalize">{activeLoan.loan_type}</span>} />
                                <Row label="Sanctioned" value={currency(activeLoan.amount)} />
                                <Row label="EMI" value={currency(activeLoan.emi)} />
                                <Row label="Tenure" value={`${activeLoan.tenure_months} mo`} />
                                <Row label="Interest" value={`${activeLoan.interest_rate}% p.a.`} />
                                <Row label="Status" value={<Badge variant="outline" className={`${STATUS_STYLES[activeLoan.status]} capitalize`}>{activeLoan.status}</Badge>} />
                                <div className="komma-gold-hairline my-4" />
                                <Button onClick={() => download(activeLoan.id, activeLoan.loan_number)}
                                    className="w-full bg-komma-gold hover:bg-komma-gold-dark text-komma-navy"
                                    data-testid="customer-download-pdf">
                                    <Download size={14} className="mr-2" /> Download Sanction Letter
                                </Button>
                            </div>

                            <div className="komma-card p-6 lg:col-span-2" data-testid="customer-emi-panel">
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">EMI Schedule</p>
                                    <span className="text-xs text-gray-500">{paidCount} / {loanPayments.length} paid</span>
                                </div>
                                <div className="komma-gold-hairline my-4" />
                                {loanPayments.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Clock className="mx-auto text-komma-gold" size={28} />
                                        <p className="text-sm text-gray-500 mt-3">Schedule will be published once the loan is disbursed.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                            <MiniStat label="Next EMI" value={upcoming ? currency(upcoming.amount) : "—"} />
                                            <MiniStat label="Due On" value={upcoming ? new Date(upcoming.due_date).toLocaleDateString() : "—"} />
                                            <MiniStat label="Total Outstanding" value={currency(totalDue)} accent />
                                        </div>
                                        <div className="overflow-hidden rounded-md border border-gray-100">
                                            <table className="w-full text-sm">
                                                <thead className="bg-komma-navy/5">
                                                    <tr>{["#", "Due Date", "Amount", "Status"].map((h) => (
                                                        <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-komma-navy">{h}</th>
                                                    ))}</tr>
                                                </thead>
                                                <tbody>
                                                    {loanPayments.map((p) => {
                                                        const overdue = !p.paid && new Date(p.due_date) < new Date();
                                                        return (
                                                            <tr key={p.id} className="border-t border-gray-100">
                                                                <td className="px-3 py-2 text-xs">{p.installment}</td>
                                                                <td className="px-3 py-2 text-xs text-komma-navy">{new Date(p.due_date).toLocaleDateString()}</td>
                                                                <td className="px-3 py-2 text-xs font-semibold">{currency(p.amount)}</td>
                                                                <td className="px-3 py-2">
                                                                    {p.paid ? (
                                                                        <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> Paid</span>
                                                                    ) : overdue ? (
                                                                        <span className="text-xs text-red-700">Overdue</span>
                                                                    ) : (
                                                                        <span className="text-xs text-gray-500">Upcoming</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between py-2 border-b border-gray-100 last:border-0 text-sm">
            <span className="text-gray-500 text-xs uppercase tracking-widest">{label}</span>
            <span className="text-komma-navy font-medium">{value}</span>
        </div>
    );
}

function MiniStat({ label, value, accent }) {
    return (
        <div className="rounded-md border border-gray-100 p-3 bg-komma-cream/40">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
            <p className={`font-serif text-lg mt-1 ${accent ? "text-komma-gold-dark" : "text-komma-navy"}`}>{value}</p>
        </div>
    );
}
